/**
 * The lifecycle of the connection dialog's animations (Option C, C1b, plan §3.2 "Lifecycle" and "settle()"). No DOM:
 * it holds Animation-like objects, so the generation guard and `settle()` are unit-tested with fakes.
 *
 * - **Generation guard.** `begin()` starts a flight: it cancels the previous generation's animations and bumps the
 *   generation; every animation tracked after it is tagged `conn-dialog:<generation>` (e2e waits filter on that prefix,
 *   never `document.getAnimations()` — `first-connect-pulse` runs forever). A late `finished` of an older generation is
 *   a no-op, and a rejected `finished` (`AbortError` on cancel) is swallowed.
 * - **Cleanup** (`onCleanup`: the root loses `data-flipping`, ghost layers lose their inline width) runs once every
 *   animation of the current generation has finished or been cancelled — or at once when a flight tracked nothing.
 * - **`settle()`**, the one way to end a flight early, is synchronous: it calls `finish()` on every animation of the
 *   current generation (each layer lands on its end state, the layout React already committed), runs the cleanup
 *   inline without waiting for `finished`, and bumps the generation so the pending callbacks become no-ops. Anything
 *   that changes the layout outside an explicit navigation — a filter keystroke, a resize, a nested dialog opening, a
 *   reduced-motion change — calls it before its state update.
 * - `cancelAll()` (unmount) cancels instead of finishing; `cancelWhere()` drops some animations of a running flight (a
 *   rail pick during the flight: the two items' surface fades).
 */
export const ANIMATION_ID_PREFIX = 'conn-dialog:';

export type FlipAnimation = {
  id: string;
  finished: Promise<unknown>;
  finish: () => void;
  cancel: () => void;
};

/** What an animation moves: the item key and layer name it targets (if any) and the animated properties. */
export type AnimationMeta = { key?: string; layer?: string; props: readonly string[] };

type Tracked = { animation: FlipAnimation; meta: AnimationMeta };

const quietly = (action: () => void) => {
  try {
    action();
  } catch {
    // An animation whose target left the document can refuse finish(); its end state is moot then.
  }
};

export type FlipController = ReturnType<typeof createFlipController>;

export function createFlipController(onCleanup: () => void) {
  let generation = 0;
  let tracked: Tracked[] = [];
  let pending = 0;
  let active = false;

  const cleanup = () => {
    active = false;
    pending = 0;
    tracked = [];
    onCleanup();
  };

  const settleOne = (owner: number) => {
    if (owner !== generation || !active) return;
    pending -= 1;
    if (pending <= 0) cleanup();
  };

  return {
    generation: () => generation,
    /** A flight (or a pane swap) is running. */
    isActive: () => active,
    begin(): number {
      const previous = tracked;
      tracked = [];
      pending = 0;
      generation += 1;
      active = true;
      previous.forEach(({ animation }) => quietly(() => animation.cancel()));
      return generation;
    },
    track(animation: FlipAnimation, meta: AnimationMeta): void {
      const owner = generation;
      animation.id = `${ANIMATION_ID_PREFIX}${owner}`;
      tracked.push({ animation, meta });
      pending += 1;
      animation.finished.then(
        () => settleOne(owner),
        () => settleOne(owner),
      );
    },
    /** After a flight issued its animations: a flight that tracked nothing cleans up at once. */
    endIfIdle(): void {
      if (active && pending === 0) cleanup();
    },
    settle(): void {
      if (!active) return;
      const current = tracked;
      current.forEach(({ animation }) => quietly(() => animation.finish()));
      cleanup();
      generation += 1;
    },
    cancelAll(): void {
      const current = tracked;
      generation += 1;
      current.forEach(({ animation }) => quietly(() => animation.cancel()));
      if (active) cleanup();
    },
    cancelWhere(predicate: (meta: AnimationMeta) => boolean): void {
      const dropped = tracked.filter(({ meta }) => predicate(meta));
      tracked = tracked.filter((entry) => !dropped.includes(entry));
      // Their rejected `finished` still counts them down (`settleOne`).
      dropped.forEach(({ animation }) => quietly(() => animation.cancel()));
    },
  };
}
